import * as _ from 'underscore';
import { appStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { Formatters, Messages } from '../../codegen/Localize';
import { ILicenseTierFilter } from '../filters/LicenseTierFilter';
import { ISiteRoleFilter } from '../filters/SiteRoleFilter';
import { CommonErrorCode, GroupErrorCode, LdapErrorCode, UserErrorCode } from '../helpers/ErrorCodes';
import { UserActionNotificationSuccessCode } from '../helpers/SuccessCodes';
import { L10n } from '../L10n/L10n';
import { IUserInfo } from '../models/IUserInfo';
import { IErrorItem, MultipleItemResult } from '../models/MultipleItemResult';
import { IIdItemInfo, INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { LicensingTier, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { IUpdateServerUsersSiteMembershipResultCodes } from '../ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IUpdateUsersGroupMembershipResult } from '../ServerApi/requests/UpdateUsersGroupMembershipRequest';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { IUser } from '../ServerApi/types/IUser';
import { IResultCodes, IUserActionError } from '../ServerApi/types/IUserActionResult';
import { ResultCode } from '../services/UserActionResultCode';
import {
  ContentActionNotification,
  IMultipleResultNotificationOptions,
  MultipleResultNotificationOptions,
} from './ContentActionNotification';
import {
  DetailType,
  ISiteMembershipDetail,
  MultipleSiteRoleMembershipActionNotification,
} from './MultipleSiteRoleMembershipActionNotification';
import { ISiteRoleChange } from './SiteRolesPickerClientService';
import { ToasterService, ToastType } from './ToasterService';

// Notification For:
// => generic user actions.

export interface UserActionErrorNotificationParams {
  users: Set<IUser>;          // the users the action was taken on.
  errors: IUserActionError[];           // the errors objects from the API response.

  // TODO: this can be removed
  showErrorMsgFunc?: (errorMsg: string) => void;  // a fn to call on error (instead of toasting); optional.
}

export interface UserActionNotificationParams extends UserActionErrorNotificationParams {
  results: IResultCodes[];              // the result objects from the API response.
  successCode: string;                            // the result code which indicates total success.
  siteRole?: string;                  //Site role that the user is set to when they are added.
}

export class UserActionNotification {

  public static $inject = [
    '$translate',
    '$filter',
    'ToasterService',
    'SiteRoleOptionsResource'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $filter: ng.IFilterService,
    private toaster: ToasterService,
    private siteRolesOptionsResource: SiteRoleOptionsResource) {}

  public notify(params: UserActionNotificationParams) {
    _.defaults(params, {
      results: [],
      errors: [],
    });

    this.notifyErrors(params);

    // handle results (successes & partial successes)
    const errorsByUserId = _.groupBy(params.errors, 'id');
    const usersMappedByResults = this.getUsersByResultMap(params.users, params.results, params.successCode, errorsByUserId);
    _.each(usersMappedByResults, (usersForResult: IUser[], resultCode: string) => {
      const toastType = (resultCode === params.successCode)
        ? ToastType.Info
        : ToastType.Error;

      // TODO: refactor getUsersByResultMap() to make this not necessary.
      const users = _.unique(usersForResult);
      let msg;
      if(resultCode === ResultCode.InsufficientLicense.toString()){ //Insufficient licenses
        msg = this.getErrorMessageForInsufficientLicenses(params.siteRole, users);
      } else {
        msg = (users.length > 1)
          ? this.multiResultMessageFor(resultCode, users.length)
          : this.resultMessageFor(resultCode, nameFor(users[0], this.$translate));
      }
      this.toaster.pop({ type: toastType, text: msg });
    });

  }

  private getErrorMessageForInsufficientLicenses(siteRole: string, users: IUser[]): string{
    //get the license tier
    const siteRoleOption = this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(siteRole);
    const translatedLicenseRole = this.$filter<ILicenseTierFilter>('licenseTier')(siteRoleOption.licenseTierRole);
    //single user vs multi-user
    if(users.length > 1){
      return Messages.userActionBulkOperationResult_31({COUNT: users.length, LICENSE_TIER: translatedLicenseRole, COUNT_LOC: Formatters.formatCount(users.length)});
    } else {
      return Messages.userActionResult_31({ USER_NAME: L10n.elideIfLong(nameFor(users[0], this.$translate)) , LICENSE_TIER: translatedLicenseRole});
    }
  }

  public notifyErrors(params: UserActionErrorNotificationParams) {
    _.defaults(params, {
      errors: [],
    });

    // handle errors (failures)
    if (params.errors.length > 0) {
      _.each(params.errors, (error: IUserActionError) => {
        const user = params.users.get(error.id);
        const nameForMsg = (user && nameFor(user, this.$translate)) || error.username;

        if (_.isFunction(params.showErrorMsgFunc)) {
          params.showErrorMsgFunc(this.errorMessageFor(error, nameForMsg));
        } else {
          this.toaster.pop({ type: ToastType.Error, text: this.errorMessageFor(error, nameForMsg)});
        }
      });
    }
  }

  /*
   * TODO: we need to know the action that was being attempted in order to produce
   *       more specific error messages.
   *       The design of this class should probably be reworked to handle that.
   */
  private errorMessageFor(error: IError, username: string): string {
    const errorCode = error.code;

    if (LdapErrorCode[errorCode]) {
      return this.ldapErrorMessage(error, username);
    }

    switch (errorCode) {
      case UserErrorCode.UserAlreadyExists:
        return this.$translate.instant('errorUserAlreadyExists', { NEW_ITEM_NAME: L10n.elideIfLong(username) });
      case UserErrorCode.UserNotSupportedGuestOperation:
        return this.$translate.instant('errorUserNotSupportedGuestOperation');
      case UserErrorCode.UserNotSupportedSelfOperation:
        return this.$translate.instant('errorUserNotSupportedSelfOperation');
      case UserErrorCode.UserInsufficientSiteQuota:
        return this.$translate.instant('errorUserInsufficientSiteQuota');
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return this.$translate.instant('errorInsufficientPermision', { USER_NAME: L10n.elideIfLong(username) });
      case CommonErrorCode.ADUserNotFound:
        return this.$translate.instant('errorUserNotFoundInAD', { USER_NAME: L10n.elideIfLong(username) });
      case CommonErrorCode.UnexpectedErrorOccurred:
        return this.$translate.instant('unexpectedErrorOnServer');
      case CommonErrorCode.SystemUserCannotBeSiteSAMLUser:
        return this.$translate.instant('systemAdminNotAllowedAsSiteSAMLUser');
      case UserErrorCode.UserPasswordNotRespectingPolicies:
        return this.$translate.instant('errorUserPasswordNotRespectingPolicies');
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

  private ldapErrorMessage(error: IError, username: string): string {
    const errorCode = error.code;

    switch (errorCode) {
      case LdapErrorCode.LdapServerDown:
        return this.$translate.instant('errorLdapServerDown', { USER_NAME: L10n.elideIfLong(username) });
      case LdapErrorCode.LdapInvalidCredentials:
        return this.$translate.instant('errorLdapInvalidCredentials', { USER_NAME: L10n.elideIfLong(username) });
      default:
        return this.$translate.instant('errorUnknownLdapError', { USER_NAME: L10n.elideIfLong(username) });
    }
  }

  private resultMessageFor(resultCode: string, username: string): string {
    return this.$translate.instant('userActionResult_'+resultCode, { USER_NAME: L10n.elideIfLong(username) });
  }

  private multiResultMessageFor(resultCode: string, count: number): string {
    return this.$translate.instant('userActionBulkOperationResult_'+resultCode, { COUNT: count }, L10n.Interpolations.Count);
  }

  // returns a dictionary of users keyed by result code.
  // each user might appear multiple times (multiple result codes per user).
  // if a user is not associated with any result codes, this function associates them with the success code.
  private getUsersByResultMap(users: Set<IUser>, results: IResultCodes[], successCode: string, errorsByUserId: _.Dictionary<IUserActionError[]>): _.Dictionary<IUser[]> {
    const resultSet = Set.fromIdItemArray(results);

    const map: _.Dictionary<IUser[]> = {};
    users.each((user: IUser) => {
      const resultsForThisUser: IResultCodes = resultSet.get(user.id);
      const resultCodesForThisUser: number[] = resultsForThisUser && resultSet.get(user.id).codes || [];

      if (resultCodesForThisUser.length > 0) {
        // there were partial success results for this user.  map them.
        _.each(resultCodesForThisUser, (resultCode: number) => {
          // Check for op codes in the results array that are considered errors.
          // Map non-errors to the sucess result code.
          const resultToNotifyFor = this.isErrorResult(resultCode) ? resultCode.toString() : successCode;

          if (map[resultToNotifyFor]) {
            map[resultToNotifyFor].push(user);
          } else {
            map[resultToNotifyFor] = [user];
          }
        });
      } else if (!errorsByUserId[user.id]) {
        // If there are no result codes or error codes for this user, treat operation as a success.
        if (map[successCode]) {
          map[successCode].push(user);
        } else {
          map[successCode] = [user];
        }
      }
    });
    return map;
  }

  private isErrorResult(resultCode: number): boolean {
    switch (resultCode) {
      case 14: // user that owns content on site/server, gets unlicensed instead
      case 15: // server admin that owns content on site/server cannot be deleted
      case 32: // site at quota
      case 31: // not enough licenses to add to site
      case 42: // not enough licenses to promote to admin
      case 52: // can't operate on self
        return true;
      default:
        return false;
    }
  }

}

Module.instance().service('UserActionNotification', UserActionNotification);

// Notification For:
// => adding users to site

export class AddUserToSiteActionNotification {

  public static $inject = [
    '$translate',
    'ToasterService',
    'SiteRoleOptionsResource'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private toaster: ToasterService,
    private siteRoleOptions: SiteRoleOptionsResource) {}

  public notify(
    showErrorMsgFunc: (errorMsg: string) => void,
    result: IResult,
    username: string,
    siteRoleEnum: string) {

    if (!!result.errors && result.errors.length > 0) {
      const errorMsg: string = this.errorMessageFor(result.errors[0], username);
      showErrorMsgFunc(errorMsg);
    } else {
      const translatedSiteRole = this.$translate.instant(this.siteRoleOptions.getSiteRoleLockey(siteRoleEnum));
      const successMsg = this.$translate.instant('addUserToSiteWithSiteRoleSuccessMsg', { NEW_ITEM_NAME: L10n.elideIfLong(username), SITE_ROLE: translatedSiteRole });
      this.toaster.pop({ type: ToastType.Info, text: successMsg });
    }
  }

  private errorMessageFor(error: IError, username: string) {
    switch (error.code) {
      case CommonErrorCode.SystemUserNotFound:
        return this.$translate.instant('errorSystemUserNotFound', { USER_NAME: L10n.elideIfLong(username) });
      case UserErrorCode.UserAlreadyExists:
        return this.$translate.instant('errorUserAlreadyExists', { NEW_ITEM_NAME: L10n.elideIfLong(username) });
      case UserErrorCode.UserInsufficientSiteQuota:
        return this.$translate.instant('errorUserInsufficientSiteQuota');
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

}

Module.instance().service('AddUserToSiteActionNotification', AddUserToSiteActionNotification);


// Notification For:
// => site role change for single site

export class SiteRolesUpdateActionNotification {

  public static $inject = [
    '$filter',
    '$translate',
    'ContentActionNotification',
    'SiteRoleOptionsResource'];

  constructor(
    private $filter: ng.IFilterService,
    private $translate: ng.translate.ITranslateService,
    private contentActionNotification: ContentActionNotification,
    private siteRoleOptionsResource: SiteRoleOptionsResource) {}

  public notify(
    result: IResult,
    users: Set<IUserInfo>,
    siteRoleEnum: string) {

    const translatedSiteRole = this.$filter<ISiteRoleFilter>('siteRole')(siteRoleEnum);

    const successfulName = getSuccessfulName(users, result, this.$translate);
    const failingName = getFailingName(users, result, this.$translate);

    // create a notification object for result reporting
    const notification: IMultipleResultNotificationOptions = MultipleResultNotificationOptions.create(
      new MultipleItemResult(users, result),
      {
        describeSuccessSummary: (successCount: number) => {
          return this.$translate.instant('updateSiteRoleSuccess', {
            SUCCESS_COUNT: successCount,
            USER_NAME: L10n.elideIfLong(successfulName),
            SITE_ROLE: translatedSiteRole
          }, L10n.Interpolations.Count);
        },
        describeErrorSummary: (errorCount: number) => {
          return this.$translate.instant('updateSiteRoleError', {
            ERROR_COUNT: errorCount,
            USER_NAME: L10n.elideIfLong(failingName),
            SITE_ROLE: translatedSiteRole
          }, L10n.Interpolations.Count);
        },
        describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
          return {
            itemName: errorItem.item.username,
            itemUrl: getUrlForUser(<IUserInfo>errorItem.item),
            errorMessage: this.errorMessageFor(errorItem, this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(siteRoleEnum).licenseTierRole)
          };
        }
      }
    );

    // pass the notification object to the service for displaying
    this.contentActionNotification.notify(notification);
  }

  private errorMessageFor(errorItem: IErrorItem<INamedItemInfo>, licenseTier: LicensingTier) {
    const translatedLicenseRole = this.$filter<ILicenseTierFilter>('licenseTier')(licenseTier);
    switch (errorItem.error.code) {
      case UserErrorCode.UserInsufficientLicenses:
        return Messages.errorUserInsufficientLicensesInTier_updateSiteRole({LICENSE_TIER: translatedLicenseRole});
      case UserErrorCode.UserNotSupportedGuestOperation:
        return Messages.errorUserNotSupportedGuestOperation();
      case UserErrorCode.UserNotSupportedAdminOperation:
        return Messages.errorUserNotSupportedAdminOperation();
      case UserErrorCode.UserNotSupportedSelfOperation:
        return Messages.errorUserNotSupportedSelfOperation();
      case UserErrorCode.UserInsufficientSiteQuota:
        return Messages.errorUserInsufficientSiteQuota();
      case UserErrorCode.UserCannotChangeFromSupportUser:
        return Messages.errorUserCannotChangeFromSupportUser();
      default:
        return Messages.errorUnknown();
    }
  }

}

Module.instance().service('SiteRolesUpdateActionNotification', SiteRolesUpdateActionNotification);


// Notification For:
// => site membership and roles change for single site

export class SiteMembershipUpdateActionNotification {

  public static $inject = [
    'MultipleSiteRoleMembershipActionNotification',
    '$filter',
    'SiteRoleOptionsResource'];

  constructor(
    private multipleSiteRoleActionNotification: MultipleSiteRoleMembershipActionNotification,
    private $filter: ng.IFilterService,
    private siteRolesOptionsResource: SiteRoleOptionsResource) {}

  public notify(
      results: IUpdateServerUsersSiteMembershipResultCodes[],
      users: Set<IUserInfo>,
      siteRoleChanges: _.Dictionary<ISiteRoleChange>): void {

    const {siteSuccessDetails, siteErrorDetails} = this.setupSiteMembershipResults(results, users, siteRoleChanges);

    if(siteSuccessDetails.length === 1){
      this.multipleSiteRoleActionNotification.notifySuccessSummary(this.getSingleMessage(siteSuccessDetails[0]));
    } else{
      this.multipleSiteRoleActionNotification.notifySuccessResult(siteSuccessDetails);
    }

    if(siteErrorDetails.length === 1){
      this.multipleSiteRoleActionNotification.notifyErrorSummary(this.getSingleMessage(siteErrorDetails[0]));
    } else {
      this.multipleSiteRoleActionNotification.notifyErrorResult(siteErrorDetails);
    }
  }

  private setupSiteMembershipResults(results: IUpdateServerUsersSiteMembershipResultCodes[], usersMap: Set<IUserInfo>, siteRoleChanges: _.Dictionary<ISiteRoleChange>){
    const siteSuccessDetails: ISiteMembershipDetail[] = [];
    const siteErrorDetails: ISiteMembershipDetail[] = [];

    for(const result of results){
      const user = usersMap.get(result.id);

      //get sucess and error messages mapped to more than one result codes
      this.getSuccessOrErrorDetailsMappedToMultipleResultCodes(result.codes, siteSuccessDetails , siteErrorDetails, user, siteRoleChanges[result.siteId].siteRole, siteRoleChanges[result.siteId].siteName);

      //compute the remaining result codes
      for(const code of result.codes){
        const siteRole = siteRoleChanges[result.siteId].siteRole;
        const siteName = siteRoleChanges[result.siteId].siteName;
        const detailItem = this.createSuccessOrErrorDetails(user, code, siteRole, siteName);
        if (detailItem && detailItem.detailType === DetailType.Success) {
          siteSuccessDetails.push(detailItem);
        } else if (detailItem && detailItem.detailType === DetailType.Error) {
          siteErrorDetails.push(detailItem);
        }
      }
    }

    return {siteSuccessDetails, siteErrorDetails};
  }

  private getSuccessOrErrorDetailsMappedToMultipleResultCodes(codes: number[], siteSuccessDetails: ISiteMembershipDetail[], siteErrorDetails: ISiteMembershipDetail[], user: IUserInfo, siteRole: string, siteName: string){

    const userAdded = codes.indexOf(UserActionNotificationSuccessCode.UserAdded) > -1;
    const userAssigned = codes.indexOf(UserActionNotificationSuccessCode.UserAssigned) > -1;
    const serverInsufficientLicenses = codes.indexOf(UserErrorCode.ServerUserInsufficientLicenses) > -1;
    if((userAdded && userAssigned) || (userAdded && serverInsufficientLicenses)){
      if(userAssigned){
        codes.splice(codes.indexOf(UserActionNotificationSuccessCode.UserAssigned), 1);
        siteSuccessDetails.push(this.createSuccessOrErrorDetails(user,UserActionNotificationSuccessCode.UserAssigned, siteRole, siteName, true));
      } else {
        codes.splice(codes.indexOf(UserErrorCode.ServerUserInsufficientLicenses), 1);
        siteErrorDetails.push(this.createSuccessOrErrorDetails(user,UserErrorCode.ServerUserInsufficientLicenses, siteRole, siteName, true));
      }
      codes.splice(codes.indexOf(UserActionNotificationSuccessCode.UserAdded), 1);
    }
  }

  private createSuccessOrErrorDetails(user: IUserInfo, code: number, siteRole: string, siteName: string, isUserAdded = false): ISiteMembershipDetail {
    const { detailType, message } = this.getSuccessOrErrorStatusWithMessage(code, user.username, siteRole, isUserAdded);
    if (detailType == null) {
      return null;
    }

    return {
      detailType: detailType,
      userName: user.username,
      siteName: siteName,
      message: message,
      userInfo: user,
      siteRole: siteRole,
      code: code,
      isUserAdded: isUserAdded
    };
  }

  private getSuccessOrErrorStatusWithMessage(code: number, username: string, siteRole: string, isUserAdded: boolean) {
    const translatedRole = this.$filter<ISiteRoleFilter>('siteRole')(siteRole);
    if(!isUserAdded){
      switch (code) {
        case UserActionNotificationSuccessCode.UserRemoved:
          return {detailType: DetailType.Success, message: Messages.singleUserRemoved()};
        case UserActionNotificationSuccessCode.UserAdded:
          return {detailType: DetailType.Success, message: Messages.singleUserAdded()};
        case UserActionNotificationSuccessCode.UserAssigned:
          return {detailType: DetailType.Success, message: Messages.userAssignedTo({SITE_ROLE: translatedRole})};
        case CommonErrorCode.SystemUserNotFound:
          return {detailType: DetailType.Error, message: Messages.errorSystemUserNotFound({ USER_NAME: L10n.elideIfLong(username) })};
        case UserErrorCode.ServerUserInsufficientLicenses:
          const licenseTier = this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(siteRole).licenseTierRole;
          const translatedLicenseRole = this.$filter<ILicenseTierFilter>('licenseTier')(licenseTier);
          return {detailType: DetailType.Error, message: Messages.errorUserInsufficientLicenses_updateSiteRole({SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole })};
        case UserErrorCode.UserNotSupportedGuestOperation:
          return {detailType: DetailType.Error, message: Messages.errorUserNotSupportedGuestOperation()};
        case UserErrorCode.ServerUserInsufficientSiteQuota:
          return {detailType: DetailType.Error, message: Messages.errorUserInsufficientSiteQuota()};
        case UserErrorCode.UserAlreadyExists:
          return {detailType: DetailType.Error, message: Messages.userNameExistsOnServerForAddUserToSiteSAML({USER_NAME: L10n.elideIfLong(username)})};
        case UserErrorCode.UserCannotChangeFromSupportUser:
          return {detailType: DetailType.Error, message: Messages.errorUserCannotChangeFromSupportUser()};
        case UserErrorCode.UserNotDeleted:
          return {detailType: DetailType.Error, message: Messages.userNotRemoved()};
        case UserErrorCode.UserUnlicensed:
          return {detailType: DetailType.Error, message: Messages.userNotRemoved()};
        default:
          return {detailType: null, message: null};
      }
    } else {
      switch(code){
      case UserActionNotificationSuccessCode.UserAssigned:
        return {detailType: DetailType.Success, message: Messages.userAddedAndAssignedTo({SITE_ROLE: translatedRole})};
      case UserErrorCode.ServerUserInsufficientLicenses:
        const licenseTier = this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(siteRole).licenseTierRole;
        const translatedLicenseRole = this.$filter<ILicenseTierFilter>('licenseTier')(licenseTier);
        return {detailType: DetailType.Error, message: Messages.errorUserInsufficientLicenses_OnAddUser({SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole })};
      default:
        return {detailType: null, message: null};
      }
    }
  }

  private getSingleMessage(detail: ISiteMembershipDetail){
    const translatedRole = this.$filter<ISiteRoleFilter>('siteRole')(detail.siteRole);
    const licenseTier = this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(detail.siteRole).licenseTierRole;
    const translatedLicenseRole = this.$filter<ILicenseTierFilter>('licenseTier')(licenseTier);
    if(!detail.isUserAdded){
      switch (detail.code) {
        case UserActionNotificationSuccessCode.UserRemoved:
          return Messages.userRemovedFrom({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName});
        case UserActionNotificationSuccessCode.UserAdded:
          return Messages.singleUserAddedToSite({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName});
        case UserActionNotificationSuccessCode.UserAssigned:
          return Messages.singleUserAssignedTo({USER_NAME: L10n.elideIfLong(detail.userName), SITE_ROLE: translatedRole, SITE_NAME: detail.siteName});
        case CommonErrorCode.SystemUserNotFound:
          return Messages.errorSystemUserNotFound({ USER_NAME: L10n.elideIfLong(detail.userName) });
        case UserErrorCode.ServerUserInsufficientLicenses:
          return Messages.errorUserInsufficientLicenses_singleUserSiteRole({USER_NAME: L10n.elideIfLong(detail.userName), SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole, SITE_NAME: detail.siteName});
        case UserErrorCode.UserNotSupportedGuestOperation:
          return Messages.errorUserNotSupportedGuestOperation();
        case UserErrorCode.ServerUserInsufficientSiteQuota:
          return Messages.errorUserInsufficientSiteQuota_singleUserSiteRole({USER_NAME: L10n.elideIfLong(detail.userName), SITE_ROLE: translatedRole, SITE_NAME: detail.siteName});
        case UserErrorCode.UserAlreadyExists:
          return Messages.userNameExistsOnServerForAddUserToSiteSAML({USER_NAME: L10n.elideIfLong(detail.userName)});
        case UserErrorCode.UserCannotChangeFromSupportUser:
          return Messages.errorUserCannotChangeFromSupportUser();
        case UserErrorCode.UserNotDeleted:
          return Messages.userNotRemovedFrom({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName});
        case UserErrorCode.UserUnlicensed:
          return Messages.userNotRemovedFrom({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName});
        default:
          return Messages.errorUnknown();
      }
    } else { // user added
        switch(detail.code){
          case UserActionNotificationSuccessCode.UserAssigned:
            return Messages.singleUserAddedAndAssignedTo({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName, SITE_ROLE: translatedRole});
          case UserErrorCode.ServerUserInsufficientLicenses:
            return Messages.errorUserInsufficientLicenses_singleUserSiteRoleOnAddUser({USER_NAME: L10n.elideIfLong(detail.userName), SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole,  SITE_NAME: detail.siteName});
          default:
            return Messages.errorUnknown();
        }
    }
  }
}

Module.instance().service('SiteMembershipUpdateActionNotification', SiteMembershipUpdateActionNotification);


// Notification For:
// => single or multiple server admin update

export class ServerAdminUpdateActionNotification {

  public static $inject = [
    '$filter',
    '$translate',
    'ContentActionNotification',
    'SiteRoleOptionsResource'];

  constructor(
    private $filter: ng.IFilterService,
    private $translate: ng.translate.ITranslateService,
    private contentActionNotification: ContentActionNotification,
    private siteRolesOptionsResource: SiteRoleOptionsResource) {}

  public notify(
    result: IResult,
    users: Set<IUserInfo>,
    newAdminState: boolean) {

    let successId: string;
    let errorId: string;

    if (newAdminState) {
      successId = 'serverAdminPromotionSuccess';
      errorId = 'serverAdminPromotionFailed';
    } else {
      successId = 'serverAdminDemotionSuccess';
      errorId = 'serverAdminDemotionFailed';
    }

    const successfulName = getSuccessfulName(users, result, this.$translate);
    const failingName = getFailingName(users, result, this.$translate);

    // create a notification object for result reporting
    const notification: IMultipleResultNotificationOptions = MultipleResultNotificationOptions.create(
      new MultipleItemResult(users, result),
      {
        describeSuccessSummary: (successCount: number) => {
          const messageArgs = {
            USER_COUNT: successCount,
            USER_NAME: L10n.elideIfLong(successfulName)
          };
          return this.$translate.instant(successId, messageArgs, L10n.Interpolations.Count);
        },
        describeErrorSummary: (errorCount: number) => {
          const messageArgs = {
            USER_COUNT: errorCount,
            USER_NAME: L10n.elideIfLong(failingName)
          };
          return this.$translate.instant(errorId, messageArgs, L10n.Interpolations.Count);
        },
        describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
          return {
            itemName: errorItem.item.username,
            itemUrl: getUrlForUser(<IUserInfo>errorItem.item),
            errorMessage: this.errorMessageFor(errorItem)
          };
        }
      }
    );

    // pass the notification object to the service for displaying
    this.contentActionNotification.notify(notification);
  }

  private errorMessageFor(errorItem: IErrorItem<IUserInfo>) {
    switch (errorItem.error.code) {
      case UserErrorCode.UserInsufficientLicenses:
        const translatedRole = this.$filter<ISiteRoleFilter>('siteRole')(SiteRoles.ServerAdministrator);
        const licenseTier = this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(SiteRoles.ServerAdministrator).licenseTierRole;
        const translatedLicenseRole = this.$filter<ILicenseTierFilter>('licenseTier')(licenseTier);

        return this.$translate.instant('errorUserInsufficientLicenses_updateSiteRole', { SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole });
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return this.$translate.instant('errorCallingUserInsufficientPermissions_serverAdminChange');
      case CommonErrorCode.SystemUserNotFound:
        return this.$translate.instant('errorSystemUserNotFound', { USER_NAME: L10n.elideIfLong(errorItem.item.username) });
      case UserErrorCode.UserNotSupportedGuestOperation:
        return this.$translate.instant('errorUserNotSupportedGuestOperation');
      case UserErrorCode.UserNotSupportedSelfOperation:
        return this.$translate.instant('errorUserNotSupportedSelfOperation');
      case UserErrorCode.UserInsufficientSiteQuota:
        return this.$translate.instant('errorUserInsufficientSiteQuota');
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

}

Module.instance().service('ServerAdminUpdateActionNotification', ServerAdminUpdateActionNotification);


// Notification For:
// => group membership for users

export class GroupMembershipUpdateActionNotification {

  public static $inject = [
    '$translate',
    'ContentActionNotification'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private contentActionNotification: ContentActionNotification) {}

  public notify(
    result: IUpdateUsersGroupMembershipResult,
    users: Set<IUserInfo>,
    addedToGroupCount: number,
    addedToGroupName: string,
    removedFromGroupCount: number,
    removedFromGroupName: string): void {

    let notification: IMultipleResultNotificationOptions;

    if (result.errors && result.errors.length && result.errors[0].code === CommonErrorCode.GroupNotFound) {
      // TODO: how can we determine if the "to" group or the "from" group (or both) are not found?
      notification = {
        errorSummary: this.$translate.instant('errorGroupNotFound', {
          ITEM_NAME: L10n.elideIfLong(addedToGroupName || removedFromGroupName)
        })
      };
    }
    else {

      let successId: string;
      let errorId: string;
      const messageArgs = {
        USER_NAME: L10n.elideIfLong(nameFor(users.first(), this.$translate))
      };

      // determine result notification strings and args
      if (addedToGroupCount > 0) {
        if (removedFromGroupCount > 0) {
          successId = 'groupMembershipChanged_usersAddedToGroupsAndRemovedFromGroups';
          errorId = 'groupMembershipChanged_usersAddedToGroupsAndRemovedFromGroups_failed';
          angular.extend(messageArgs,
            {
              ADD_COUNT: addedToGroupCount,
              ADD_NAME: L10n.elideIfLong(addedToGroupName),
              REMOVE_COUNT: removedFromGroupCount,
              REMOVE_NAME: L10n.elideIfLong(removedFromGroupName)
            });
        } else {
          successId = 'groupMembershipChanged_usersAddedToGroups';
          errorId = 'groupMembershipChanged_usersAddedToGroups_failed';
          angular.extend(messageArgs,
            {
              ADD_COUNT: addedToGroupCount,
              ADD_NAME: L10n.elideIfLong(addedToGroupName)
            });
        }
      }
      else {
        successId = 'groupMembershipChanged_usersRemovedFromGroups';
        errorId = 'groupMembershipChanged_usersRemovedFromGroups_failed';
        angular.extend(messageArgs,
          {
            REMOVE_COUNT: removedFromGroupCount,
            REMOVE_NAME: L10n.elideIfLong(removedFromGroupName)
          });
      }

      // TODO: this doesn't seem right - do the errors apply to users or groups? I don't think
      // we can calculate the success count if errors can apply to groups.
      notification = MultipleResultNotificationOptions.create(
        new MultipleItemResult(users, result),
        {
          describeSuccessSummary: (successCount: number) => {
            angular.extend(messageArgs, {USER_COUNT: successCount});
            return this.$translate.instant(successId, messageArgs, L10n.Interpolations.Count);
          },
          describeErrorSummary: (errorCount: number) => {
            angular.extend(messageArgs, {USER_COUNT: errorCount});
            return this.$translate.instant(errorId, messageArgs, L10n.Interpolations.Count);
          },
          describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
            return {
              itemName: errorItem.item.username,
              itemUrl: getUrlForUser(<IUserInfo>errorItem.item),
              errorMessage: this.errorMessageFor(errorItem)
            };
          }
        }
      );
    }

    // pass the notification object to the service for displaying
    this.contentActionNotification.notify(notification);
  }

  private errorMessageFor(errorItem: IErrorItem<IUserInfo>) {
    switch (errorItem.error.code) {
      case GroupErrorCode.InvalidGroupMembershipUpdate:
        return this.$translate.instant('errorInvalidAllUsersGroupMembershipUpdate');
      case CommonErrorCode.SiteUserNotFound:
        return this.$translate.instant('errorUserNotFound');
      case CommonErrorCode.SystemUserNotFound:
        return this.$translate.instant('errorSystemUserNotFound', { USER_NAME: L10n.elideIfLong(errorItem.item.username) });
      case CommonErrorCode.GroupNotFound:
        return this.$translate.instant('errorGroupNotFound');
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

}

Module.instance().service('GroupMembershipUpdateActionNotification', GroupMembershipUpdateActionNotification);

// helpers

const nameFor = (user: IUserInfo, $translate: ng.translate.ITranslateService): string => {
  return $translate.instant('userActionNameFormattedForToast', { DISPLAY_NAME: L10n.elideIfLong(user.displayName), USER_NAME: L10n.elideIfLong(user.username) });
};

// get name of a successful item
// returns empty string if no items successful
const getSuccessfulName = (users: Set<IUserInfo>, result: IResult, $translate: ng.translate.ITranslateService): string => {
  const successfulItem = getSuccessfulItem(users, result);
  return successfulItem ? nameFor(successfulItem, $translate) : '';
};

// get name of a failing item
// returns empty string if no items failed
const getFailingName = (users: Set<IUserInfo>, result: IResult, $translate: ng.translate.ITranslateService): string => {
  const failingItem = getFailingItem(users, result);
  return failingItem ? nameFor(failingItem, $translate) : '';
};

// get a successful item, useful when there is only one item that is successful
// returns undefined if no items successful
const getSuccessfulItem = (users: Set<IUserInfo>, result: IResult): IUserInfo => {
  return users.find((item: IIdItemInfo) => {
    let matchingError: IError = undefined;
    if (result.errors) {
      matchingError = _.find(result.errors, (error: IError) => {
        return item.id === error.id;
      });
    }
    return !matchingError;
  });
};

// get a failing item, useful when there is only one item that is failing
// returns undefined if no items failed
const getFailingItem = (users: Set<IUserInfo>, result: IResult): IUserInfo => {
  return users.find((item: IIdItemInfo) => {
    let matchingError: IError = undefined;
    if (result.errors) {
      matchingError = _.find(result.errors, (error: IError) => {
        return item.id === error.id;
      });
    }
    return !!matchingError;
  });
};

// get the URL to the specified user's profile page
const getUrlForUser = (user: IUserInfo) => {
  if (user.username === 'guest' && user.domainName === 'local') {
    return null;
  } else {
    const siteUrls = siteUrlsSelector(appStore.getState());
    return siteUrls.userUrl(user.username, user.domainName);
  }
};
